import PropTypes from 'prop-types';
import { Controller, useFormContext } from 'react-hook-form';
import { RadioGroup } from '@material-ui/core';
import NgpField from './NgpField';

/**
 * Converts the raw MUI radio event value to the form field value.
 * - 'boolean' mode: converts string "true"/"false" to actual boolean (default, for backward compat)
 * - 'string' mode: passes the raw string value through unchanged
 */
const toFormValue = (eventValue, valueType) => (valueType === 'string' ? eventValue : eventValue === 'true');

/**
 * Converts the form field value to the string representation required by MUI RadioGroup.
 * - 'boolean' mode: converts boolean to "true"/"false" string
 * - 'string' mode: returns the string as-is, falling back to empty string
 */
const toDisplayValue = (formValue, valueType) => (valueType === 'string' ? formValue || '' : String(formValue));

/**
 * Managed radio group backed by react-hook-form.
 *
 * valueType="boolean" (default): field stores an actual boolean; radio buttons use value="true"/"false".
 * valueType="string": field stores the raw string; radio buttons use arbitrary string values.
 *
 * Extra props (e.g. aria-label) are forwarded to MUI RadioGroup.
 */
export default function NgpRadioGroup({ fieldName, children, disabled = false, valueType = 'boolean', ...rest }) {
    const { control, errors } = useFormContext();
    const defaultValue = valueType === 'string' ? '' : false;

    return (
        <NgpField errors={errors} fieldName={fieldName}>
            <Controller
                control={control}
                name={fieldName}
                defaultValue={defaultValue}
                render={({ value = defaultValue, onChange }) => (
                    <RadioGroup
                        value={toDisplayValue(value, valueType)}
                        onChange={(e) => onChange(toFormValue(e.target.value, valueType))}
                        disabled={disabled}
                        {...rest}
                    >
                        {children}
                    </RadioGroup>
                )}
            />
        </NgpField>
    );
}

NgpRadioGroup.propTypes = {
    fieldName: PropTypes.string.isRequired,
    children: PropTypes.node.isRequired,
    disabled: PropTypes.bool,
    valueType: PropTypes.oneOf(['boolean', 'string']),
};

NgpRadioGroup.defaultProps = {
    disabled: false,
    valueType: 'boolean',
};
